/**
 * Runs Okta execution after Claude returns `executeAction`, then sends a follow-up user message to the architect.
 */
import { executeAction } from "./executionEngine.js";
import { fetchExecutionFeedback } from "./fetchExecutionFeedback.js";

function getCommonCauses(actionType) {
  const causes = {
    create_saml_app:
      "invalid SSO URL format, app name already exists, insufficient token permissions (needs okta.apps.manage)",
    create_oidc_app: "invalid redirect URI, app name taken, token missing okta.apps.manage scope",
    create_swa_app: "invalid target URL, app name taken, or token missing okta.apps.manage scope",
    create_user: "email already exists in org, invalid email format, login must be email format",
    enable_threatinsight: "token lacks security policy permissions",
    update_password_policy: "token lacks policy management permissions",
    add_oin_app:
      "OIN schema fetch failed, missing required settings for POST /api/v1/apps, catalog app name wrong for this org, or token lacks okta.apps.manage",
    enable_deprovisioning: "app API limitations or insufficient scope for app provisioning features",
    list_groups:
      "invalid q/search expression, token missing okta.groups.read, or org URL mismatch",
    search_groups:
      "invalid q/search expression, token missing okta.groups.read, or org URL mismatch",
    assign_app_to_group:
      "wrong appId/groupId, assignment already exists, or token lacks okta.apps.manage / group permissions",
    get_app_groups: "invalid appId or token missing okta.apps.read",
    create_and_assign_group:
      "duplicate group name, invalid appId, or assignment failed after group was created (see partial response)",
    get_app_metadata: "invalid appId, token missing okta.apps.read, or app not found",
    list_password_policies: "token missing policy read scope or org URL mismatch",
    update_password_policy: "invalid policyId, token lacks policy manage, or Okta rejected merged settings",
    list_auth_policies: "token missing policy read or ACCESS_POLICY not available for this org",
    list_signin_policies: "token missing policy read",
    list_authenticators: "token missing authenticators read",
    activate_authenticator: "invalid authenticatorId or token lacks authenticator admin",
    deactivate_authenticator: "invalid authenticatorId or token lacks authenticator admin",
    list_mfa_policies: "token missing policy read",
    update_mfa_policy: "invalid policyId or token lacks MFA policy manage",
    scan_policies: "one or more policy list calls failed — check token scopes",
    list_apps:
      "token missing okta.apps.read, invalid q/filter expression, or org URL mismatch",
    scan_apps:
      "token missing okta.apps.read, invalid q/filter expression, or org URL mismatch",
    query_system_log:
      "token lacks logs.read scope, invalid filter expression, or time range too large",
    search_logs:
      "token lacks logs.read scope, invalid filter expression, or time range too large",
    get_logs:
      "token lacks logs.read scope, invalid filter expression, or time range too large",
    fix_all_weak_password_policies:
      "token missing okta.policies.manage, or one or more policies are not editable (e.g. locked system policy)",
    update_all_weak_password_policies:
      "token missing okta.policies.manage, or one or more policies are not editable (e.g. locked system policy)",
    update_all_password_policies:
      "token missing okta.policies.manage, or one or more policies are not editable (e.g. locked system policy)",
  };
  return causes[actionType] || "check token permissions and parameter format";
}

function buildInlineSuccessContext(action, result) {
  if (result?.pendingApproval === true && result?.proposal && typeof result.proposal === "object") {
    const pr = /** @type {Record<string, unknown>} */ (result.proposal);
    const id = pr.proposalId != null ? String(pr.proposalId) : "(pending)";
    const rev = pr.reversibility != null ? String(pr.reversibility) : "unknown";
    const op = action?.type || (pr.operationId != null ? String(pr.operationId) : "operation");
    return (
      `[SYSTEM: Proposal ${id} is queued for review. ` +
      `Tell the user a change has been prepared and they should review it in the Approvals drawer. ` +
      `Do NOT enumerate every field — the drawer shows the diff. ` +
      `High level: ${op} (${rev}). ` +
      `Ask if they want a conceptual walkthrough or prefer to review in the drawer first.]`
    );
  }

  const app = result.app || result.user || result.group || result.policy || result;
  const t = action?.type;
  switch (t) {
    case "create_saml_app":
    case "create_oidc_app":
    case "create_swa_app":
    case "add_oin_app":
      return (
        `[SYSTEM: SUCCESS — App created in Okta. ` +
        `Name: "${app?.label || action.params?.appName}", ` +
        `ID: ${app?.id}, Status: ${app?.status ?? "—"}, Sign-on: ${app?.signOnMode ?? action.type}. ` +
        `Now confirm to the user, show them the key ` +
        `configuration values they need to complete ` +
        `setup on the app side, and suggest next steps.]`
      );
    case "create_user":
      return (
        `[SYSTEM: SUCCESS — User created in Okta. ` +
        `Name: ${action.params?.firstName} ${action.params?.lastName}, ` +
        `Email: ${action.params?.email}, ` +
        `ID: ${result.user?.id || app?.id}. ` +
        `Confirm and suggest next steps: assign to groups, ` +
        `trigger MFA enrollment.]`
      );
    case "enable_threatinsight":
      return (
        `[SYSTEM: SUCCESS — ThreatInsight set to ENFORCE mode. ` +
        `Suspicious sign-ins will now be blocked. ` +
        `Confirm to the user and mark this as complete.]`
      );
    case "update_password_policy":
    case "fix_all_weak_password_policies":
    case "update_all_weak_password_policies":
    case "update_all_password_policies": {
      const results = Array.isArray(result.results) ? result.results : [];
      if (results.length === 0 && result.message) {
        return `[SYSTEM: ${result.message}]`;
      }
      const succeeded = results.filter((r) => r.ok);
      const failedRows = results.filter((r) => !r.ok);
      return (
        `[SYSTEM: Policy update complete.\n` +
        `Updated ${succeeded.length} of ${results.length} policies:\n` +
        `${succeeded.map((r) => `✅ "${r.name}" — new minLength: ${r.newMinLength ?? "—"}`).join("\n")}\n` +
        `${failedRows.length ? `${failedRows.map((r) => `❌ "${r.name}" — ${r.error || "unknown"}`).join("\n")}\n` : ""}` +
        `NOW: Execute list_password_policies one final time to confirm the changes persisted in Okta. ` +
        `Then tell the user exactly which policies are now confirmed compliant based on the live read. ` +
        `If any still show minLength < 12, report them as failed even if the API returned 200.]`
      );
    }
    case "enable_deprovisioning":
      return (
        `[SYSTEM: SUCCESS — Deprovisioning run completed. ` +
        `Result: ${JSON.stringify(result)}. ` +
        `Confirm what was fixed and what may still need attention.]`
      );
    case "assign_app_to_group":
      return `[SYSTEM: SUCCESS — App assignment to group completed. Confirm to the user.]`;
    case "list_groups":
    case "search_groups": {
      const groups = Array.isArray(result.groups) ? result.groups : [];
      const preview = groups
        .slice(0, 12)
        .map((g) => `"${g?.name || g?.id}" (${g?.id})`)
        .join(", ");
      return (
        `[SYSTEM: SUCCESS — Found ${groups.length} group(s). ` +
        `Use these real ids in assign_app_to_group (do not guess): ${preview || "(none)"}. ` +
        `If none match, refine q/search.]`
      );
    }
    case "get_app_groups": {
      const ag = Array.isArray(result.groups) ? result.groups : [];
      return `[SYSTEM: SUCCESS — App has ${ag.length} group assignment row(s). Raw: ${JSON.stringify(ag).slice(0, 1200)}]`;
    }
    case "create_and_assign_group":
      return (
        `[SYSTEM: SUCCESS — Created group "${result.group?.name}" and assigned to app ${result.appId}. ` +
        `Confirm to the user; if provisioning is on, note downstream account creation. ` +
        `Then give SP-side SSO checklist steps (Salesforce or vendor-specific) and optionButtons including show-saml-metadata / mark-salesforce-sso-complete where applicable.]`
      );
    case "get_app_metadata":
      return (
        `[SYSTEM: SUCCESS — App record fetched. Metadata URL: ${result.metadataUrl || "—"}, ` +
        `SSO/app link: ${result.ssoUrl || "—"}. Present copy-paste URLs and next SP-side steps.]`
      );
    case "list_password_policies":
    case "list_password_policy":
    case "get_password_policies": {
      const policies = Array.isArray(result.policies) ? result.policies : [];
      if (!policies.length) {
        return (
          `[SYSTEM: No password policies found in org. ` +
          `This is unusual — tell the user and investigate.]`
        );
      }
      const weak = policies.filter((p) => !p.isStrong);
      const allStrong = weak.length === 0;

      if (allStrong) {
        return (
          `[SYSTEM: Password policy verification complete.\n` +
          `All ${policies.length} policies meet BP-AUTH-03 standard:\n` +
          `${policies.map((p) => `✅ "${p.name}" — minLength: ${p.settings?.minLength}`).join("\n")}\n\n` +
          `STOP. Do not update anything. Do not execute any \n` +
          `further actions. Tell the user all password policies \n` +
          `are confirmed compliant and mark the roadmap item \n` +
          `complete. Set roadmapUpdate for password-policy-hardening\n` +
          `with status: complete.]`
        );
      }

      const weakDetails = weak
        .map(
          (p) =>
            `"${p.name}" id=${p.id} minLength=${p.settings?.minLength} symbol=${p.settings?.requireSymbol}`,
        )
        .join("\n");

      return (
        `[SYSTEM: ${weak.length} of ${policies.length} password policies are below BP-AUTH-03:\n` +
        `${weakDetails}\n` +
        `Execute update_password_policy now.]`
      );
    }
    case "list_authenticators":
    case "list_mfa_factors":
    case "list_factors": {
      const auths = Array.isArray(result.authenticators) ? result.authenticators : [];
      const active = Array.isArray(result.active) ? result.active : [];
      const inactive = Array.isArray(result.inactive) ? result.inactive : [];
      const lines = auths
        .map(
          (a) =>
            `· ${a.name} (${a.key}) — ${a.isActive ? "Active" : "Inactive"}` +
            `${a.isPhishingResistant ? " 🛡️ Phishing-resistant" : ""}`,
        )
        .join("\n");
      const missing = [];
      if (!result.hasFido2) missing.push("FIDO2/WebAuthn (phishing-resistant)");
      if (!result.hasOktaVerify) missing.push("Okta Verify");
      return (
        `[SYSTEM: Authenticators retrieved.\n${lines}\n` +
        `Active: ${active.length}, Inactive: ${inactive.length}.\n` +
        `${missing.length ? `Missing recommended: ${missing.join(", ")}` : "All recommended authenticators active."}\n` +
        `Present this clearly. Flag any phishing-resistant gaps. Offer to activate missing authenticators.]`
      );
    }
    case "list_auth_policies":
    case "list_authentication_policies": {
      const policies = Array.isArray(result.policies) ? result.policies : [];
      const policyLines = policies
        .map((p) => {
          const ruleCount = p.rules?.length || 0;
          const hasStrongAuth = p.rules?.some(
            (r) => r.actions?.appSignOn?.verificationMethod?.type === "ASSURANCE",
          );
          return (
            `· "${p.name}" — ${ruleCount} rule(s) — ` +
            `${p.status} — ${hasStrongAuth ? "✅ MFA enforced" : "⚠️ Check MFA rules"}`
          );
        })
        .join("\n");
      return (
        `[SYSTEM: Authentication policies retrieved:\n${policyLines}\n` +
        `Present these clearly. Flag any without MFA enforcement. ` +
        `Offer to review rules or configure step-up authentication.]`
      );
    }
    case "list_signin_policies":
    case "list_legacy_policies": {
      const policies = Array.isArray(result.policies) ? result.policies : [];
      const active = policies.filter((p) => p.status === "ACTIVE");
      return (
        `[SYSTEM: Legacy sign-on policies retrieved.\n` +
        `Total: ${policies.length}, Active: ${active.length}.\n` +
        `${active.map((p) => `· "${p.name}" (ACTIVE — should be migrated)`).join("\n")}\n` +
        `${active.length > 0
          ? "These are legacy and should be migrated to Authentication Policies. Explain the risk (BP-SIGNON-01) and offer to help migrate."
          : "Good — no active legacy policies found."}\n` +
        `Present this clearly to the user.]`
      );
    }
    case "list_mfa_policies": {
      const policies = Array.isArray(result.policies) ? result.policies : [];
      return (
        `[SYSTEM: MFA enrollment policies retrieved (${policies.length}). ` +
        `Summarize each policy name, status, and rule count. ` +
        `Explain that these control which factors users must enroll in.]`
      );
    }
    case "scan_policies":
    case "audit_policies":
      return (
        `[SYSTEM: Policy scan complete. Password policies: ${(result.passwordPolicies || []).length}, ` +
        `authentication policies: ${(result.authPolicies || []).length}, ` +
        `legacy sign-on policies: ${(result.legacyPolicies || []).length} ` +
        `(${result.totalLegacyActive || 0} active legacy). ` +
        `Present a concise health report and offer fixes.]`
      );
    case "list_apps":
    case "scan_apps": {
      const apps = Array.isArray(result.apps) ? result.apps : [];
      const preview = apps
        .slice(0, 15)
        .map((a) => `"${a?.label || a?.name || a?.id}" (${a?.id})`)
        .join(", ");
      return (
        `[SYSTEM: SUCCESS — Listed ${apps.length} application(s). ` +
        `Use real app ids (0oa...) from this list for assignments: ${preview || "(none)"}.]`
      );
    }
    case "query_system_log":
    case "search_logs":
    case "get_logs": {
      const events = Array.isArray(result.events) ? result.events : [];
      if (!events.length) {
        return (
          `[SYSTEM: No log events found matching the query. ` +
          `Tell the user no events were found and offer to broaden the search criteria.]`
        );
      }
      const lines = events.slice(0, 10).map((e) => {
        const ts = e.published ? new Date(e.published).toLocaleString() : "?";
        const actor = e.actor?.displayName || e.actor?.alternateId || "Unknown";
        const out = e.outcome?.result || "";
        const reason = e.outcome?.reason ? ` (${e.outcome.reason})` : "";
        return `· ${ts} — ${e.eventType} — ${actor} — ${out}${reason}`;
      });
      return (
        `[SYSTEM: System log query returned ${events.length} events:\n` +
        `${lines.join("\n")}\n` +
        `Present these clearly to the user. ` +
        `If there are failures or anomalies, highlight them. ` +
        `Offer to filter further or investigate specific events.]`
      );
    }
    case "update_mfa_policy":
      return `[SYSTEM: SUCCESS — MFA enrollment policy updated. Policy name: "${result.policy?.name}". Confirm details to the user.]`;
    case "activate_authenticator":
      return `[SYSTEM: SUCCESS — Authenticator activated: ${result.authenticator?.name || action.params?.authenticatorId}. Confirm to the user.]`;
    case "deactivate_authenticator":
      return `[SYSTEM: SUCCESS — Authenticator deactivated: ${result.authenticator?.name || action.params?.authenticatorId}. Confirm to the user.]`;
    default:
      return null;
  }
}

function buildInlineErrorContext(action, result) {
  const t = action?.type;
  const pwdTypes =
    t === "update_password_policy" ||
    t === "fix_all_weak_password_policies" ||
    t === "update_all_weak_password_policies" ||
    t === "update_all_password_policies";
  const rows = Array.isArray(result?.results) ? result.results : [];
  if (pwdTypes && rows.length > 0 && !rows.some((r) => r.ok)) {
    return (
      `[SYSTEM_ERROR: All policy updates failed.\n` +
      `${rows.map((r) => `"${r.name}": ${r.error || "unknown"}`).join("\n")}\n` +
      `Explain each failure. Common causes:\n` +
      `  - Token missing okta.policies.manage scope\n` +
      `  - Policy is system-managed and cannot be modified\n` +
      `  - Field name mismatch in PUT body\n` +
      `Offer to diagnose or try a different approach.]`
    );
  }
  return null;
}

/**
 * @param {object} action
 * @param {object} result
 * @param {boolean} ok
 */
async function resolveFollowUpText(action, result, ok) {
  const inline = ok ? buildInlineSuccessContext(action, result) : buildInlineErrorContext(action, result);
  if (inline) return inline;
  return fetchExecutionFeedback({
    providerId: "okta",
    operationKind: action?.type,
    operationResult: result,
    action,
    success: ok,
  });
}

/**
 * @param {object} opts
 * @param {{ type: string, params?: object, description?: string }} opts.action
 * @param {object | null} opts.project
 * @param {function} opts.setMessages
 * @param {(text: string, o?: object) => Promise<unknown>} opts.sendMessage
 * @param {(action: { type: string, params?: object }, result: object, ok: boolean) => void} [opts.onExecutionResult]
 * @param {() => Promise<unknown>} [opts.refreshProject] Reload project after queued propose (Approvals drawer)
 */
export async function orchestrateExecuteAction({
  action,
  project,
  setMessages,
  sendMessage,
  onExecutionResult,
  refreshProject,
}) {
  if (!action?.type) return;

  // Registry sanity check (Week 1): ensure every executeAction is tagged for Week 2 approval gating.
  if (import.meta.env.DEV && typeof window !== "undefined" && window.__idwizOpRegistry) {
    if (!window.__idwizOpRegistry[action.type]) {
      // eslint-disable-next-line no-console
      console.warn("[operations/registry] Missing entry for executeAction:", action.type);
    }
  }

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console -- execution trace
    console.log("[executionEngine] Executing:", action.type, action.params);
  }

  const execId = `exec-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const label = action.description || action.type;

  setMessages((prev) => [
    ...prev,
    {
      role: "system",
      id: execId,
      content: `⚡ Executing: ${label}...`,
      isExecuting: true,
      timestamp: Date.now(),
    },
  ]);

  const result = await executeAction(action, project);

  setMessages((prev) => prev.filter((m) => m.id !== execId));

  const ok =
    result?.pendingApproval === true ||
    result?.ok === true ||
    result?._status === 200 ||
    (result?.user?.id != null && result?.ok !== false) ||
    (result?.app?.id != null && result?.ok !== false) ||
    (result?.group?.id != null && result?.ok !== false) ||
    (Array.isArray(result?.events) && result?.ok === true) ||
    (Array.isArray(result?.apps) && result?.ok === true) ||
    (Array.isArray(result?.results) && result?.ok === true);

  if (typeof onExecutionResult === "function") {
    try {
      onExecutionResult(action, result, ok);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[executionOrchestrator] onExecutionResult:", e);
    }
  }

  if (ok && result?.pendingApproval === true && typeof refreshProject === "function") {
    try {
      await refreshProject();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[executionOrchestrator] refreshProject after propose:", e);
    }
  }

  const text = await resolveFollowUpText(action, result, ok);

  /** @type {{ interactionMode?: string, _isSystemMessage?: boolean, auditFindings?: unknown }} */
  const followUpOpts = {
    interactionMode: "chat",
    _isSystemMessage: true,
  };
  if (ok && action.type === "comprehensive_assessment") {
    const audit =
      result && typeof result === "object" && result.audit && typeof result.audit === "object"
        ? result.audit
        : result;
    if (audit?.findings) {
      followUpOpts.interactionMode = "tenant_connected";
      followUpOpts.auditFindings = audit.findings;
    }
  }

  await sendMessage(text, followUpOpts);
}
